
import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { LazyImage } from '@/components/ui/lazy-image';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { ImageOff, Search, Trash2, RefreshCw } from 'lucide-react';

interface ImageItem {
  name: string;
  url: string;
  size?: number;
  created_at?: string;
}

export default function ImageGallery() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [images, setImages] = useState<ImageItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (user) {
      fetchImages();
    }
  }, [user]);

  const fetchImages = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from('images')
        .list(user.id, {
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        throw error;
      }

      const imageList = await Promise.all(
        data
          .filter(file => file.name.match(/\.(jpeg|jpg|gif|png|webp)$/i))
          .map(async (file) => {
            const { data: urlData } = supabase.storage
              .from('images')
              .getPublicUrl(`${user.id}/${file.name}`);
            
            return {
              name: file.name,
              url: urlData.publicUrl,
              size: file.metadata?.size,
              created_at: file.created_at
            };
          })
      );

      setImages(imageList);
    } catch (error: any) {
      console.error('Error fetching images:', error);
      toast({
        title: 'Error loading images',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!user || !selectedImage) return;
    
    try {
      const { error } = await supabase.storage
        .from('images')
        .remove([`${user.id}/${selectedImage.name}`]);

      if (error) throw error;

      setImages(images.filter(image => image.name !== selectedImage.name));
      toast({
        title: 'Image deleted',
        description: 'The image was successfully deleted.'
      });
    } catch (error: any) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Failed to delete image',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedImage(null);
    }
  };

  const confirmDeleteImage = (image: ImageItem) => {
    setSelectedImage(image);
    setDeleteDialogOpen(true);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const filteredImages = images.filter(image => 
    image.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Image Gallery</h1>
        <Button 
          onClick={fetchImages} 
          variant="outline"
          disabled={isLoading}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex space-x-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search images by filename..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Badge variant="outline">
          {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Images</TabsTrigger>
          <TabsTrigger value="grid">Grid View</TabsTrigger>
        </TabsList>
        
        <TabsContent value="all" className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredImages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredImages.map((image) => (
                <Card key={image.name} className="overflow-hidden">
                  <div className="aspect-square relative group">
                    <LazyImage
                      src={image.url}
                      alt={image.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => confirmDeleteImage(image)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <div className="text-sm font-medium truncate" title={image.name}>
                      {image.name}
                    </div>
                    <div className="text-xs text-muted-foreground flex justify-between mt-1">
                      <span>{formatFileSize(image.size)}</span>
                      <span>{formatDate(image.created_at)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <ImageOff className="mx-auto h-12 w-12 opacity-20 mb-2" />
              <p className="text-lg">No images found</p>
              <p className="text-sm">
                {searchQuery ? 'Try a different search term' : 'Upload some images to get started'}
              </p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="grid">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredImages.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {filteredImages.map((image) => (
                <div 
                  key={image.name}
                  className="aspect-square relative group rounded-md overflow-hidden border"
                >
                  <LazyImage
                    src={image.url}
                    alt={image.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Button
                      variant="destructive"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => confirmDeleteImage(image)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <ImageOff className="mx-auto h-12 w-12 opacity-20 mb-2" />
              <p className="text-lg">No images found</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this image. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteImage} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
